import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, UserX } from "lucide-react";

// ============================================================================
// AbsenceTemplatesAdmin — Abwesenheits-Vorlagen für den Dienstplan
// Vorlage = vordefinierte Abwesenheit (Frei, Urlaub, Zeitausgleich, Krank, …)
// Wird im Dienstplan per Klick auf eine leere Zelle als Auswahl angeboten.
// ============================================================================
const EMPTY = { name: "", shift_type: "frei", notes_template: "", color: "#71717a" };

const ABSENCE_TYPES = [
  { key: "frei",            label: "Frei",                color: "#71717a" },
  { key: "urlaub",          label: "Urlaub",              color: "#10b981" },
  { key: "zeitausgleich",   label: "Zeitausgleich",       color: "#06b6d4" },
  { key: "krank",           label: "Krank / Krankenstand", color: "#ef4444" },
  { key: "schulung",        label: "Schulung",            color: "#f59e0b" },
  { key: "sonderurlaub",    label: "Sonderurlaub",        color: "#a855f7" },
  { key: "feiertag",        label: "Feiertag",            color: "#ec4899" },
  { key: "unbezahlt_frei",  label: "Unbezahlt frei",      color: "#52525b" },
  { key: "karenz",          label: "Karenz",              color: "#d946ef" },
  { key: "unentschuldigt",  label: "Unentschuldigt",      color: "#e11d48" },
];

const PRESETS = [
  { name: "Frei",          shift_type: "frei",          color: "#71717a" },
  { name: "Urlaub",        shift_type: "urlaub",        color: "#10b981" },
  { name: "Zeitausgleich", shift_type: "zeitausgleich", color: "#06b6d4" },
  { name: "Krank",         shift_type: "krank",         color: "#ef4444" },
  { name: "Schulung",      shift_type: "schulung",      color: "#f59e0b" },
  { name: "Feiertag",      shift_type: "feiertag",      color: "#ec4899" },
  { name: "Sonderurlaub",  shift_type: "sonderurlaub",  color: "#a855f7" },
];

export default function AbsenceTemplatesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/absence-templates");
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.name.trim()) { toast.error("Name ist Pflicht"); return; }
    try {
      if (editing) await api.put(`/absence-templates/${editing.id}`, form);
      else await api.post("/absence-templates", form);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  async function remove(id) {
    if (!window.confirm("Vorlage löschen?")) return;
    try { await api.delete(`/absence-templates/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  async function seedDefaults() {
    try {
      for (const p of PRESETS) {
        await api.post("/absence-templates", { ...p, notes_template: "" });
      }
      toast.success(`${PRESETS.length} Standard-Vorlagen angelegt`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Anlegen der Standard-Vorlagen"); }
  }

  function startEdit(t) {
    setEditing(t);
    setForm({ ...EMPTY, ...t });
    setShowForm(true);
  }
  function startNew() {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  }

  const typeLabel = (key) => ABSENCE_TYPES.find(t => t.key === key)?.label || key;

  return (
    <div data-testid="absence-templates-admin">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-2.5">
          <UserX className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0"/>
          <div>
            <h3 className="font-display font-semibold text-zinc-50">Abwesenheits-Vorlagen</h3>
            <p className="text-xs text-zinc-500 mt-0.5 max-w-xl">
              Definiere hier die Abwesenheitsarten (Frei, Urlaub, Zeitausgleich, Krank, …). Sie erscheinen im Dienstplan unter <span className="text-zinc-400">„Schicht setzen → Arbeitet NICHT"</span> und in der Quick-Abwesenheit oben.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {items.length === 0 && (
            <button onClick={seedDefaults} data-testid="absence-seed-defaults"
              className="px-3 py-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium">
              Standard-Vorlagen anlegen
            </button>
          )}
          <button onClick={startNew} data-testid="absence-tpl-new"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Plus className="w-3.5 h-3.5"/> Neue Vorlage
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-zinc-500 text-sm mb-2">Noch keine Abwesenheits-Vorlagen.</div>
            <div className="text-xs text-zinc-600 max-w-md mx-auto">
              Tipp: Klick auf <span className="text-emerald-400 font-medium">„Standard-Vorlagen anlegen"</span> für einen Schnellstart (Frei, Urlaub, Zeitausgleich, Krank, Schulung, Feiertag, Sonderurlaub).
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Typ</th>
                <th className="px-4 py-3 text-left">Standard-Notiz</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} data-testid={`absence-tpl-row-${t.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 text-zinc-100">
                    <span className="w-1 h-5 rounded mr-2 inline-block align-middle" style={{ backgroundColor: t.color || "#f59e0b" }}/>
                    {t.name}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-300">{typeLabel(t.shift_type)}</td>
                  <td className="px-4 py-2.5 text-zinc-400 text-xs">{t.notes_template || "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => startEdit(t)} data-testid={`absence-tpl-edit-${t.id}`}
                      className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                    <button onClick={() => remove(t.id)} data-testid={`absence-tpl-del-${t.id}`}
                      className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="absence-tpl-form">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">
                {editing ? "Vorlage bearbeiten" : "Neue Abwesenheits-Vorlage"}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-zinc-200">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase text-zinc-500 block mb-1">Name</label>
                <input value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="absence-tpl-name"
                  placeholder="z.B. Urlaub, Zeitausgleich, Standard-Krankmeldung"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500 block mb-1">Abwesenheits-Typ</label>
                <select value={form.shift_type}
                  onChange={(e) => {
                    const k = e.target.value;
                    const preset = ABSENCE_TYPES.find(t => t.key === k);
                    setForm({ ...form, shift_type: k, color: preset?.color || form.color });
                  }}
                  data-testid="absence-tpl-type"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                  {ABSENCE_TYPES.map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500 block mb-1">Standard-Notiz (optional)</label>
                <textarea value={form.notes_template}
                  onChange={(e) => setForm({ ...form, notes_template: e.target.value })}
                  data-testid="absence-tpl-notes"
                  rows={3}
                  placeholder="z.B. Krankenstand bis auf Widerruf"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500 block mb-1">Farbe</label>
                <input type="color" value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  data-testid="absence-tpl-color"
                  className="w-16 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer"/>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
              <button onClick={save} data-testid="absence-tpl-save"
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
